import {
  OrganizationSwitcher,
  Show,
  UserButton,
  useAuth,
} from "@clerk/react-router";
import { Link, NavLink } from "react-router";
import { Home, Activity } from "lucide-react";

export function Header() {
  const { sessionClaims } = useAuth();

  // Internal navigation is only for RB2 org or explicit internal metadata.
  const publicMetadata = sessionClaims?.publicMetadata as
    | Record<string, unknown>
    | undefined;
  const isInternal =
    publicMetadata?.isInternal === true ||
    sessionClaims?.org_slug === "rb2";

  const navClassName = ({ isActive }: { isActive: boolean }) =>
    [
      "inline-flex items-center gap-2 rounded-[12px] px-2.5 py-2 text-sm font-medium transition-colors sm:px-3",
      isActive
        ? "bg-[#ffede7] text-[#fe150a]"
        : "text-[#431908] hover:bg-[#fff8f6] hover:text-[#fe150a]",
    ].join(" ");

  return (
    <nav className="sticky top-0 z-40 border-b border-[#efe8e5] bg-white/90 backdrop-blur-md">
      <div className="mx-auto flex h-20 w-full max-w-7xl items-center gap-2 px-3 sm:gap-4 sm:px-6 lg:px-8">
        <div className="flex min-w-0 shrink-0 items-center">
          <Link
            to="/"
            className="group min-w-0 rounded-[12px] px-1 py-1 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#fe150a]/35 focus-visible:ring-offset-2"
          >
            <div className="truncate text-lg font-semibold leading-none text-[#431908] transition-colors group-hover:text-[#fe150a]">
              Service Desk
            </div>
          </Link>
        </div>

        <Show when="signed-in">
          <div className="min-w-0 flex-1 px-1 sm:px-3">
            <div className="mx-auto w-fit max-w-full scale-[0.9] sm:scale-100">
              <OrganizationSwitcher
                hidePersonal={true}
                afterSelectOrganizationUrl="/refresh"
                skipInvitationScreen={true}
                organizationProfileMode="navigation"
                organizationProfileUrl="/org-profile"
              />
            </div>
          </div>
        </Show>

        <div className="flex shrink-0 items-center gap-4 sm:gap-6">
          <Show when="signed-in">
            <div className="flex items-center gap-1 rounded-[14px] border border-[#efe8e5] bg-white p-1 shadow-sm">
              <NavLink
                to="/"
                end
                className={navClassName}
                aria-label="Dashboard"
              >
                <Home className="h-4 w-4" />
                <span className="hidden sm:inline">Dashboard</span>
              </NavLink>

              {isInternal && (
                <NavLink
                  to="/statuspage-internal"
                  className={navClassName}
                  aria-label="Status"
                >
                  <Activity className="h-4 w-4" />
                  <span className="hidden sm:inline">Status</span>
                </NavLink>
              )}
            </div>
          </Show>

          <div className="flex items-center">
            <Show when="signed-in">
              <UserButton />
            </Show>

            <Show when="signed-out">
              <Link
                to="/sign-in"
                className="rounded-[12px] border border-[#fe150a]/30 bg-[#ffede7] px-4 py-2 text-sm font-medium text-[#fe150a] transition-colors hover:bg-[#fe150a] hover:text-white"
              >
                Sign in
              </Link>
            </Show>
          </div>
        </div>
      </div>
    </nav>
  );
}
